import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import MapApiRepository from "../repositories/MapApiRepository";
import { MapMarker } from "./elements/MapMarker";
import { useMap } from "@vis.gl/react-google-maps";
import { isRequestAborted } from "../../../helpers/ApiHelpers";
import { isObjectNull } from "components/helpers/ObjectHelpers";
import MapPropertiesService from "../services/MapPropertiesService";
import MapService, { IMapService } from "../services/MapService";
import { type Marker, MarkerClusterer } from '@googlemaps/markerclusterer';
import { useIsAisActive, useMapBounds, useMapZoom, useMapDragStart, useMapDragEnd, useMapResize, useSelectShipPosition, useSetShowLoader } from "../hooks/useMapStore";
import { MapPilotageMarkers } from "./markers/MapPilotageMarkers";
import { MapShipMarker } from "./markers/MapShipMarker";
import { useWarningNotification, AppNotificationSystemNames } from 'components/hooks/useAppStore';

const MarkerCountContent = (count: number): HTMLDivElement => {
    const content = document.createElement('div');
    content.textContent = `${count}`;
    content.classList.add('gm-custom-cluster-content');

    return content;
}

const MapCustomClusterRenderer = {
    render: ({ count, position } : { count: number; position: google.maps.LatLngLiteral }) => {
        const markerSize = Math.floor(36 + Math.sqrt(count) * 2);
        const content = document.createElement('div');
        content.classList.add('gmc-custom-cluster');
        content.style.width = `${markerSize}px`;
        content.style.height = `${markerSize}px`;

        content.appendChild(MarkerCountContent(count));

        return new google.maps.marker.AdvancedMarkerElement({
            position: position,
            content: content,
        });
    },
};

const ClusterThreshold = 10;

interface MapMarkersProps {
    // showAis?: boolean;
    pilotageId?: number,
    shipId?: number,
}

export const MapMarkers = ({
    // showAis = false,
    pilotageId = 0,
    shipId = 0,
}: MapMarkersProps) => {

    const addWarningNotification = useWarningNotification();

    const isAisActive = useIsAisActive();
    const stateBounds = useMapBounds();
    const stateZoom = useMapZoom();
    const stateDragStart = useMapDragStart();
    const stateDragEnd = useMapDragEnd();
    const stateResize = useMapResize();
    const stateSelectShipPosition = useSelectShipPosition();
    const setShowLoader = useSetShowLoader();

    const map = useMap();
    const [markers, setMarkers] = useState<{ [key: string]: Marker }>({});
    const clusterer = useMemo(() => {
        if (!map) return null;

        return new MarkerClusterer({ map, renderer: MapCustomClusterRenderer });
    }, [map]);

    const setMarkerRef = useCallback((marker: Marker | null, key: string) => {
        setMarkers(markers => {
            if ((marker && markers[key]) || (!marker && !markers[key]))
                return markers;

            if (marker) {
                return { ...markers, [key]: marker };
            } else {
                const { [key]: _, ...newMarkers } = markers;

                return newMarkers;
            }
        });
    }, []);

    const [aisMarkers, setAisMarkers] = useState<any[]>([]);
    const [pilotage, setPilotage] = useState<any>(null);
    const [selectedMmsi, setSelectedMmsi] = useState<number>(0);

    const prevZoomRef = useRef<number | null>(null);

    const componentRef: {
        current: {
            shipPositionPolyline: any,
            isAisCancelled: boolean
        }
    } = useRef({
        shipPositionPolyline: null,
        isAisCancelled: false
    });
    const { current: localRef } = componentRef;

    const mapService: IMapService = new MapService();

    const mapProperties = MapPropertiesService.properties;
    const shipTypes = MapPropertiesService.shipTypes;

    let aisSearchAbortController = new AbortController();

    const clearAisTimeout = () => {
        if ((window as any)._searchAisTimeout) {
            clearTimeout((window as any)._searchAisTimeout);
        }
    }

    const fetchPilotage = async () => {
        if (pilotageId === 0) return;

        const response: any = await MapApiRepository.getReadOnlyPilotageByIdAsync(pilotageId);
        if (response.ok) {
            const data = await response.json();
            setPilotage(data);
        }
    }

    const fetchAis = (zoom: number, excludeShipId = 0) => {
        if (!isAisActive) return;

        const canSearch = zoom && zoom >= mapProperties.MarkersVisibleAtZoom;
        if (!canSearch)  return;

        clearAisTimeout();
        
        (window as any)._searchAisTimeout = setTimeout(() => {
            searchAis(excludeShipId);
        }, 250);
    }

    const searchAis = async (excludeShipId:number) => {
        if (!map || !isAisActive) return;

        localRef.isAisCancelled = false;
        const bounds = map.getBounds();
        if (!bounds) return;
        
        aisSearchAbortController.abort();
        aisSearchAbortController = new AbortController();

        setShowLoader(true);

        const southWest = bounds.getSouthWest();
        const nothEast = bounds.getNorthEast();
        const width = nothEast.lat() - southWest.lat();
        const height = nothEast.lng() - southWest.lng();

        const markers: any[] = [];
        const response: any = await MapApiRepository.getShipPositionsWithinAreaAsync(southWest.lat(), southWest.lng(), width, height, aisSearchAbortController.signal);
        let data = [];

        if (response.ok) {
            data = await response.json();
        } else {
            OnAisResponseError(response, addWarningNotification);
        }
        
        for (const position of data) {
            if (localRef.isAisCancelled) break;

            if (excludeShipId > 0 && position.shipID === excludeShipId) continue;
            
            const latLng = new window.google.maps.LatLng(position.latitude, position.longitude);
            const existing: any = aisMarkers.find((s: any) => s.mmsi === position.mmsi);

            if (existing) {
                existing.position = latLng;
                existing.courseOverGround = position.courseOverGround;
                existing.speedOverGround = position.speedOverGround;
            } else {

                const marker: any = mapService.createShipPositionInformationObject(position, position.shipTypeIdentifier === shipTypes.PilotVessel);
                marker.shipTypeGroupIdentifier = position.shipTypeGroupIdentifier;
                marker.shipTypeIdentifier = position.shipTypeIdentifier;
                marker.length = position.length;

                markers.push(marker);
            }
        }

        setAisMarkers(prev => [...prev, ...markers]);
        setShowLoader(false);
    }

    const handleAisChanged = () => {
        if (!map || !isAisActive) return;

        const zoom = map.getZoom();
        if (!zoom) return;

        setShowLoader(false);
        localRef.isAisCancelled = true;

        if (pilotageId > 0 && isObjectNull(pilotage)) return;
        
        const excludeShipId = pilotage ? pilotage.pilotageShip.shipId : 0;
        fetchAis(zoom, excludeShipId);
    }

    useEffect(() => {
        if (!clusterer) return;

        clusterer!.clearMarkers();

        const zoom = prevZoomRef.current;
        if (zoom >= ClusterThreshold) {
            // Add markers directly to the map
            Object.values(markers).forEach(marker => marker.setMap(map));
        } else {
            // Add markers to the clusterer
            clusterer!.addMarkers(Object.values(markers));
        }

    }, [clusterer, markers]);

    useEffect(() => {
        handleOnSelect(stateSelectShipPosition);
    }, [stateSelectShipPosition]);

    useEffect(() => {
        setShowLoader(false);
        localRef.isAisCancelled = true;
    }, [stateDragStart]);

    useEffect(() => {
        handleAisChanged();

        const prevZoom = prevZoomRef.current;
        if (
            (prevZoom !== null && prevZoom < ClusterThreshold && stateZoom >= ClusterThreshold) ||
            (prevZoom !== null && prevZoom >= ClusterThreshold && stateZoom < ClusterThreshold)
        ) {
            clusterer!.clearMarkers();
        }

        if (stateZoom >= ClusterThreshold) {
            // Add markers directly to the map
            Object.values(markers).forEach(marker => marker.setMap(map));
        } else {
            // Add markers to the clusterer
            clusterer!.addMarkers(Object.values(markers));
        }

        prevZoomRef.current = stateZoom;
    }, [stateBounds, stateZoom, stateDragEnd, stateResize]);

    useEffect(() => {
        
        if (!map || !isAisActive) {
            setAisMarkers([]);
            return;
        }
        // search ais
        const zoom = map.getZoom();
        if (!zoom) return;

        fetchAis(zoom);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, isAisActive]);

    useEffect(() => {
        fetchPilotage();
        return () => {
            localRef.current = null;
            clearAisTimeout();
            aisSearchAbortController.abort();
        }
    }, []);

    return (
        <>
            <MapPilotageMarkers pilotageId={pilotageId} />  
            <MapShipMarker shipId={shipId} pilotageId={pilotageId} />  
            
            {
                aisMarkers.map((marker, index) =>
                    <MapMarker
                        key={index}
                        marker={marker}
                        selectedMmsi={selectedMmsi}
                        setMarkerRef={setMarkerRef}
                    />
                )
            }
        </>
    )

    async function handleOnSelect(e: any) {
        if (!e) {
            setSelectedMmsi(0);
            return;
        }
        if (pilotageId > 0 && isObjectNull(pilotage)) return;
        if (pilotageId > 0 && !isObjectNull(pilotage)) {
            if (pilotage.pilotageShip.mmsi === e.mmsi) {
                setSelectedMmsi(0);
                return;
            }
        }

        setSelectedMmsi(e.mmsi);

        setAisMarkers(prev => {
            // Check for duplicate in the latest state
            if (prev.some((m: any) => m.mmsi === e.mmsi)) {
                return prev;
            }
            const shipMarker: any = mapService.createShipPositionInformationObject(
                e,
                e.shipTypeIdentifier === shipTypes.PilotVessel
            );
            shipMarker.shipTypeGroupIdentifier = e.shipTypeGroupIdentifier;
            shipMarker.shipTypeIdentifier = e.shipTypeIdentifier;
            shipMarker.length = e.length;

            return [...prev, shipMarker];
        });
    }
}

const OnAisResponseError = (response: any, addWarningNotification:any) => {
    if (isRequestAborted(response)) return;
    addWarningNotification("Kunne ikke hente AIS data", AppNotificationSystemNames.MapAisFetchDataError);
}
